import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import Dropzone from "react-dropzone";
import SVG from "react-inlinesvg";
import IconButton from "src/components/IconButton";
import { cn } from "src/lib/cn";
import { IMAGE_DROPZONE_ACCEPT, handleImagesDropRejected, handleImagesDropped } from "./lib/imageDropHandlers";
import type { PendingUploadFile } from "./lib/usePendingUploadFiles";
import type { ItemSaveStatus } from "./lib/types";
import ImageSavingOverlay from "./ImageSavingOverlay";

const REMOVE_BUTTON_SPRING = { type: "spring" as const, stiffness: 500, damping: 25 };

interface EntityImageMultiUploadDropzoneProps {
  files: PendingUploadFile[];
  itemSaveStatuses: Record<string, ItemSaveStatus>;
  onFilesAdded: (files: File[]) => void;
  onRemove: (id: string) => void;
  onError: (message: string | null) => void;
}

export default function EntityImageMultiUploadDropzone({ files, itemSaveStatuses, onFilesAdded, onRemove, onError }: EntityImageMultiUploadDropzoneProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <Dropzone
      accept={IMAGE_DROPZONE_ACCEPT}
      multiple
      onDrop={(acceptedFiles) => handleImagesDropped(acceptedFiles, onFilesAdded, onError)}
      onDropRejected={() => handleImagesDropRejected(onError)}
    >
      {({ getRootProps, getInputProps, isDragActive }) =>
        files.length === 0 ? (
          <div
            {...getRootProps()}
            className={cn(
              "box-border flex h-full w-full cursor-pointer flex-col items-center justify-center gap-8 rounded-2xl border border-dashed p-4 text-center transition-colors duration-200 ease-out",
              isDragActive
                ? "border-app-primary-cta-high-contrast bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_12%,transparent)]"
                : "border-app-primary-cta-transparent-30 hover:border-app-primary-cta-high-contrast",
            )}
          >
            <input {...getInputProps()} />
            <div
              className={cn(
                "flex items-center justify-center rounded-[7px] bg-transparent transition-[box-shadow,transform] shadow-[0_4px_10px_rgba(0,0,0,0.18)] duration-200 ease-out",
                isDragActive && "scale-[1.08] -rotate-[4deg] shadow-[0_4px_10px_rgba(0,0,0,0.25)]",
              )}
            >
              <SVG
                src="/assets/image-slim.svg"
                width={68}
                height={68}
                className="[&_path]:fill-app-primary-cta-high-contrast"
                aria-hidden="true"
              />
            </div>
            <span
              className={cn(
                "pointer-events-none inline-flex items-center gap-2 rounded-[10px] bg-app-primary-cta px-4 py-2 text-sm font-medium text-app-cta-text-color transition-colors duration-200 ease-out",
                isDragActive && "bg-app-primary-cta-hover",
              )}
            >
              <SVG
                src="/assets/import-icon.svg"
                width={16}
                height={16}
                className="[&_path]:fill-current"
                aria-hidden="true"
              />
              {isDragActive ? "Drop your images here" : "Drop or click to select images"}
            </span>
          </div>
        ) : (
          <div className="grid h-full w-full auto-rows-min grid-cols-5 gap-[10px] overflow-y-auto">
            {files.map((pending) => (
              <div
                key={pending.id}
                className="relative aspect-square overflow-hidden rounded-xl border border-app-grey-3"
                onMouseEnter={() => setHoveredId(pending.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <img
                  src={pending.previewUrl}
                  alt=""
                  className="block h-full w-full object-cover"
                  aria-hidden="true"
                />
                <AnimatePresence>
                  {hoveredId === pending.id && itemSaveStatuses[pending.id] !== "saving" && itemSaveStatuses[pending.id] !== "done" && (
                    <motion.div
                      key="remove-button"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={REMOVE_BUTTON_SPRING}
                      className="absolute right-1 top-1 z-30"
                    >
                      <IconButton
                        src="/assets/close.svg"
                        size={24}
                        iconSize={12}
                        smartFill
                        ariaLabel="Remove image"
                        wrapperClassName="!size-[20px] !bg-app-grey-4 hover:!bg-app-grey-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(pending.id);
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                {itemSaveStatuses[pending.id] && <ImageSavingOverlay status={itemSaveStatuses[pending.id]} />}
              </div>
            ))}
          </div>
        )
      }
    </Dropzone>
  );
}
